import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, ErrorState, Icon, Skeleton, StatusBadge, cn, toast } from "@sagot/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { UploadRows } from "../../features/deploy-knowledge/knowledge/files-panel";
import { sourceIcon, typeOf } from "../../features/deploy-knowledge/knowledge/source-model";
import { SourceStatus } from "../../features/deploy-knowledge/knowledge/source-status";
import { useReindex } from "../../features/deploy-knowledge/knowledge/use-knowledge";
import type { useUploads } from "../../features/deploy-knowledge/knowledge/use-uploads";
import { formatBytes } from "../../features/deploy-knowledge/shared/format";
import { syncPhase } from "../../features/deploy-knowledge/shared/sync";
import type { SourceView } from "../../features/deploy-knowledge/shared/types";
import { useErrorMessage } from "../../lib/api-errors";

const K = "onboarding.knowledge";
const KB = "deployKnowledge.knowledge";
const SKELETON_ROWS = 3;

export type SourceTab = "files" | "crawl" | "qa";

function detailOf(source: SourceView): string | null {
  if (source.type === "website") return source.url;
  return source.size_bytes ? formatBytes(source.size_bytes) : null;
}

function SourceItem({ source, retrying, onRetry }: { source: SourceView; retrying: boolean; onRetry: (source: SourceView) => void }) {
  const { t } = useTranslation();
  const tile = sourceIcon(source);
  const failed = source.status === "failed";
  const warned = syncPhase(source) === "warnings";
  const detail = detailOf(source);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-3 py-2.5" data-testid="onboarding-source">
      <span className={cn("inline-flex size-9 shrink-0 items-center justify-center rounded-md", tile.tile)}>
        <Icon name={tile.icon} className="size-4" />
      </span>
      <div className="min-w-40 flex-1">
        <p className="truncate text-sm font-semibold text-fg">{source.title}</p>
        <p className="truncate text-xs text-fg-subtle">
          {t(`${KB}.types.${typeOf(source)}`)}
          {detail ? ` · ${detail}` : ""}
        </p>
        {failed ? <p className="mt-0.5 text-xs text-danger">{source.status_reason ?? t(`${K}.sourceFailedFallback`)}</p> : null}
        {warned && source.status_reason ? <p className="mt-0.5 text-xs text-warning">{source.status_reason}</p> : null}
      </div>
      {failed ? (
        <>
          <StatusBadge status="danger" label={t(`${KB}.stages.failed`)} />
          <Button variant="secondary" size="sm" loading={retrying} aria-label={t(`${K}.retrySource`, { name: source.title })} onClick={() => { onRetry(source); }}>
            <Icon name="refresh-cw" />
            {t(`${K}.retry`)}
          </Button>
        </>
      ) : (
        <SourceStatus source={source} />
      )}
    </li>
  );
}

interface SourcesListProps {
  sources: UseQueryResult<SourceView[]>;
  uploads: ReturnType<typeof useUploads>;
  onPick: (tab: SourceTab) => void;
}

/** Everything added so far (live status from the source events stream) plus uploads still in flight. */
export function SourcesList({ sources, uploads, onPick }: SourcesListProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const reindex = useReindex();
  const inFlight = uploads.items.filter((item) => item.outcome === "uploading" || item.outcome === "failed");
  const hidden = new Set(inFlight.map((item) => item.sourceId));
  const rows = (sources.data ?? []).filter((source) => !hidden.has(source.id));
  const retry = (source: SourceView) => {
    reindex.mutate(source.id, {
      onSuccess: () => { toast.info(t(`${KB}.menu.reindexStarted`)); },
      onError: (caught) => { toast.error(message(caught)); },
    });
  };
  return (
    <section aria-labelledby="onboarding-sources-title" className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 id="onboarding-sources-title" className="text-base font-bold tracking-tight">
          {t(`${K}.sourcesHeading`)}
        </h2>
        {sources.data ? <Badge tone="neutral">{rows.length + inFlight.length}</Badge> : null}
      </div>
      {inFlight.length > 0 ? <UploadRows uploads={inFlight} sources={sources.data ?? []} label={t(`${K}.uploadsLabel`)} onRetry={(key) => void uploads.retry(key)} /> : null}
      {sources.isPending ? (
        <div className="space-y-2" aria-busy="true" data-testid="sources-loading">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : sources.isError ? (
        <div className="rounded-lg border border-line">
          <ErrorState headingLevel={3} title={t(`${K}.sourcesErrorTitle`)} description={t(`${K}.sourcesErrorBody`)} retryLabel={t(`${K}.sourcesRetry`)} onRetry={() => void sources.refetch()} />
        </div>
      ) : rows.length === 0 && inFlight.length === 0 ? (
        <div className="rounded-lg border border-line">
          <EmptyState
            icon="database"
            headingLevel={3}
            title={t(`${K}.emptyTitle`)}
            description={t(`${K}.emptyBody`)}
            action={
              <>
                <Button variant="secondary" size="sm" onClick={() => { onPick("files"); }}>
                  <Icon name="upload" />
                  {t("onboarding.knowledge.tabFiles")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => { onPick("crawl"); }}>
                  <Icon name="globe" />
                  {t("onboarding.knowledge.tabCrawl")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => { onPick("qa"); }}>
                  <Icon name="message-square" />
                  {t("onboarding.knowledge.tabQa")}
                </Button>
              </>
            }
          />
        </div>
      ) : (
        <ul className="space-y-2" aria-label={t(`${K}.sourcesLabel`)}>
          {rows.map((source) => (
            <SourceItem key={source.id} source={source} retrying={reindex.isPending && reindex.variables === source.id} onRetry={retry} />
          ))}
        </ul>
      )}
    </section>
  );
}
